import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const SLOTS = ["Morning (9 AM – 12 PM)", "Afternoon (12 PM – 4 PM)", "Evening (4 PM – 8 PM)"];
const empty = { name: "", phone: "", email: "", preferred_date: "", preferred_time: SLOTS[0], message: "" };
const inputCls = "w-full bg-background border border-border focus:border-primary px-4 py-3 outline-none transition-colors text-sm";
const labelCls = "block font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2";

export default function BookingModal({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      toast.success("Request received! Adarsh's team will reach out to confirm your 1-2-1 slot.");
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-modal" className="bg-background border border-border rounded-none max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="w-12 h-12 bg-primary flex items-center justify-center mb-3">
            <CalendarClock size={22} className="text-primary-foreground" />
          </div>
          <DialogTitle className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl">
            Book your 1-2-1
          </DialogTitle>
          <p className="text-muted-foreground text-sm">
            Share your details and a preferred slot. We'll reach out to design your cashflow.
          </p>
        </DialogHeader>

        <form onSubmit={submit} data-testid="booking-form" className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone *</label>
              <input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+91..." />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="booking-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Preferred date</label>
              <input data-testid="booking-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Preferred slot</label>
              <select data-testid="booking-time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className={inputCls}>
                {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>What do you want to achieve?</label>
            <textarea data-testid="booking-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className={`${inputCls} resize-y`} placeholder="Optional" />
          </div>
          <button
            data-testid="booking-submit"
            type="submit"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wide py-4 hover:bg-[#1AE39C] transition-colors disabled:opacity-60"
          >
            {loading ? "Submitting..." : "Request My Session"}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            For educational purposes only. Investments are subject to market risks.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
